import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnResizeBar from "@shared/components/SnResizeBar";
import { menuInfo } from "@shared/type/CommonType";

interface Props {
  Menuprops: menuInfo;
}

// 입출고내역현황(선입선출) — 조회 전용.
// 상단 입고내역(FIFO_IN) 행 선택 시 하단 출고내역(FIFO_OUT)을 그 입고 lot 기준으로 필터한다.
// 연결: IN.no_io=OUT.no_io_in, IN.no_line=OUT.no_line_in (grid1은 no_io/no_line을 no_io_in/no_line_in 으로도 내려 매칭).
const SN_MTL_EVL_REP_014 = forwardRef(({ Menuprops }: Props, ref) => {
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColumns] = useState<any>({ grid1: [], grid2: [] });
  const [dataSources, setDataSources] = useState<any>({ grid1: [], grid2: [] });

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColumns({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchForm();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  const onSearch = async () => {
    try {
      const data: any = await fnGetDataSet({
        id: "sn_mtl_evl_rep_014_01",
        params: searchRef.current?.getFormdata?.() ?? {},
      });
      const grid1 = data?.[0] ?? [];
      setDataSources({ grid1, grid2: data?.[1] ?? [] });
      if (!grid1.length) {
        await showDialog("alter", "조회된 입고내역이 없습니다.");
      }
    } catch (error) {}
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        />
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="55%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "입고내역" }}
            // 입고 lot 선택 → 출고내역을 no_io_in/no_line_in 으로 필터.
            filter={{
              refs: [gridRefs.current["grid2"]],
              columns: ["no_io_in", "no_line_in"],
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="44%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "single", title: "출고내역" }}
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_MTL_EVL_REP_014;
